import { useState, useEffect, useCallback } from 'react';
import { useNetworkStatus } from './useNetworkStatus';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { logAudit } from '@/lib/audit';
import { toast } from 'sonner';
import {
  STORES,
  getPendingCount,
  getAllFromStore,
  markAsSynced,
  type PendingAttendance,
  type PendingTask,
  type PendingAssignmentUpdate,
} from '@/lib/offline-storage';
import { fetchReturningAttendeeIds } from '@/lib/attendance-utils';
import { OFFLINE_SYNC_INTERVAL_MS, OFFLINE_SYNC_ONLINE_DELAY_MS } from '@/lib/timing-constants';
import {
  generatePickupCode,
  isPickupCodeCollision,
  isRetryableCheckInRaceError,
} from '@/lib/kids-pickup-code';
import { getPickupCodeSubmission } from '@/lib/pickup-code-submission';
import { setLastCleanSync, clearLastCleanSync } from '@/hooks/useOfflineSyncStatus';
import { buildRespondRpcArgs } from '@/lib/volunteer-response';

interface SyncState {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncAt: Date | null;
  syncError: string | null;
}

interface SyncResult {
  successCount: number;
  errorCount: number;
  conflictCount: number;
}

export function useOfflineSync() {
  const { isOnline, wasOffline } = useNetworkStatus();
  const { user } = useAuth();
  const [syncState, setSyncState] = useState<SyncState>({
    isSyncing: false,
    pendingCount: 0,
    lastSyncAt: null,
    syncError: null,
  });

  const safeGetPendingCount = useCallback(async () => {
    try {
      return await getPendingCount();
    } catch (error) {
      console.error('Failed to get pending count:', error);
      setSyncState(prev => ({
        ...prev,
        syncError: 'Unable to read pending sync count. We will retry shortly.',
      }));
      return null;
    }
  }, []);

  // Update pending count periodically
  useEffect(() => {
    const updateCount = async () => {
      const count = await safeGetPendingCount();
      if (count === null) return;
      setSyncState(prev => ({ ...prev, pendingCount: count }));
    };

    updateCount();
    const interval = setInterval(updateCount, OFFLINE_SYNC_INTERVAL_MS); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [safeGetPendingCount]);

  const syncAttendance = useCallback(async (): Promise<SyncResult> => {
    const pendingAttendance = await getAllFromStore<PendingAttendance>(STORES.PENDING_ATTENDANCE);
    const unsyncedAttendance = pendingAttendance.filter(a => !a.synced);

    let successCount = 0;
    let errorCount = 0;
    const conflictCount = 0;

    const firstVisitByAttendanceId = new Map<string, boolean>();
    const attendanceByOrg = new Map<string, PendingAttendance[]>();
    unsyncedAttendance.forEach((attendance) => {
      const list = attendanceByOrg.get(attendance.organization_id) ?? [];
      list.push(attendance);
      attendanceByOrg.set(attendance.organization_id, list);
    });

    for (const [organizationId, records] of attendanceByOrg.entries()) {
      const personIds = Array.from(new Set(records.map((record) => record.person_id)));
      const returningIds = await fetchReturningAttendeeIds(organizationId, personIds);

      const recordsByPerson = new Map<string, PendingAttendance[]>();
      records.forEach((record) => {
        const list = recordsByPerson.get(record.person_id) ?? [];
        list.push(record);
        recordsByPerson.set(record.person_id, list);
      });

      recordsByPerson.forEach((recordsForPerson, personId) => {
        if (returningIds.has(personId)) {
          recordsForPerson.forEach((record) => {
            firstVisitByAttendanceId.set(record.id, false);
          });
          return;
        }

        const sorted = recordsForPerson.slice().sort((a, b) => {
          const aTime = new Date(a.event_date).getTime();
          const bTime = new Date(b.event_date).getTime();
          if (aTime !== bTime) return aTime - bTime;
          return new Date(a.check_in_time).getTime() - new Date(b.check_in_time).getTime();
        });

        sorted.forEach((record, index) => {
          firstVisitByAttendanceId.set(record.id, index === 0);
        });
      });
    }

    // Maps an original family pickup_code -> its reissued replacement, shared
    // across this whole sync pass. Pickup codes are family-scoped, so colliding
    // siblings (queued with the same code) must all converge on ONE replacement
    // — otherwise one reprinted code can't check out the whole family.
    const pickupCodeRemap = new Map<string, string>();

    for (const attendance of unsyncedAttendance) {
      try {
        let existingQuery = supabase
          .from('attendance')
          .select('id, capacity_alert_at')
          .eq('organization_id', attendance.organization_id)
          .eq('person_id', attendance.person_id);

        if (attendance.event_id) {
          // Scoped to event_date too (Codex round-33 P1 finding on #5782,
          // same class as rounds 30-32): without it, a multi-day all-day
          // event's second (or later) queued day dedupes against an
          // already-synced prior day's row and is silently marked synced
          // without ever being inserted — or, once dated rows exist for
          // more than one day, maybeSingle() throws on multiple matches.
          existingQuery = existingQuery
            .eq('event_id', attendance.event_id)
            .eq('event_date', attendance.event_date);
        } else {
          existingQuery = existingQuery
            .is('event_id', null)
            .eq('event_date', attendance.event_date)
            .eq('event_name', attendance.event_name);
          if (attendance.notes) {
            existingQuery = existingQuery.eq('notes', attendance.notes);
          } else {
            existingQuery = existingQuery.is('notes', null);
          }
        }

        const { data: existing, error: existingError } = await existingQuery.maybeSingle();
        if (existingError) {
          throw existingError;
        }
        if (existing) {
          if (existing.capacity_alert_at) {
            // Durable recovery (#4852): this row was already inserted
            // over-capacity by an earlier attempt whose RPC response never
            // reached the client (e.g. connectivity dropped mid-flush), so
            // the toast never fired then. capacity_alert_at survives that —
            // surface it now instead of silently marking synced.
            toast.error('Room is over capacity', {
              description: `${attendance.event_name} was already synced over this room's listed limit. Recount the room now.`,
              duration: Infinity,
            });
          }
          await markAsSynced(STORES.PENDING_ATTENDANCE, attendance.id);
          successCount++;
          continue;
        }

        const resolvedFirstVisit =
          firstVisitByAttendanceId.get(attendance.id) ?? attendance.is_first_visit ?? false;

        // Kids-check-in rows go through the volunteer-authorizing RPC; general
        // attendance stays on the RLS-governed insert (volunteers have
        // childcare:add but not attendance:add). The check_in_method fallback
        // covers rows queued before the kiosk_check_in flag existed.
        const isKidsRow = attendance.kiosk_check_in === true || attendance.check_in_method === 'kiosk';
        const pickupSubmission = getPickupCodeSubmission({
          // Rows queued before this flag are code-less by default. We cannot
          // safely infer a kids pickup context from legacy code/notes alone.
          pickupCodeContext: attendance.pickup_code_context === true,
          pickupCode: attendance.pickup_code,
          notes: attendance.notes,
        });
        const familyCode = pickupSubmission.pickupCode;
        let attemptCode = familyCode;
        let attemptNotes = pickupSubmission.notes;
        let codeReissued = false;
        // If a sibling sharing this family code was already reissued in this
        // sync pass, reuse the SAME replacement so the family stays on one code.
        if (familyCode && pickupCodeRemap.has(familyCode)) {
          attemptCode = pickupCodeRemap.get(familyCode)!;
          attemptNotes = attemptNotes?.match(/Pickup code:\s*\d+/i)
            ? attemptNotes.replace(/Pickup code:\s*\d+/i, `Pickup code: ${attemptCode}`)
            : attemptNotes;
          codeReissued = true;
        }
        let insertError;
        let kidsRpcResult: { over_capacity_alert?: boolean } | null = null;
        // #5903 round-11 P2: a pickup-code collision and the reservation-
        // room-change race are independent failure classes that can each
        // occur on the SAME row's sync attempt, in either order. A single
        // shared attempt budget lets handling one exhaust the retry meant
        // for the other, leaving the row failed even though both were
        // individually retryable. Track each class's own budget (one retry
        // apiece) instead of a shared attempt counter; the total-attempts
        // cap is just a defensive backstop against an unexpected error
        // shape looping forever.
        let collisionRetriesLeft = 1;
        let raceRetriesLeft = 1;
        for (let totalAttempts = 0; totalAttempts < 5; totalAttempts++) {
          const { data: attemptData, error } = isKidsRow
            ? await supabase.rpc('record_kids_check_in', {
                p_id: attendance.id,
                p_organization_id: attendance.organization_id,
                p_person_id: attendance.person_id,
                p_event_id: attendance.event_id,
                p_event_date: attendance.event_date,
                p_event_name: attendance.event_name,
                p_check_in_method: attendance.check_in_method || 'kiosk',
                p_check_in_time: attendance.check_in_time,
                p_is_first_visit: resolvedFirstVisit,
                p_notes: attemptNotes,
                p_pickup_code: attemptCode,
                p_room_id: attendance.room_id ?? null,
                // Offline replay: the child is already physically checked in
                // from earlier — a lost capacity race alerts staff instead of
                // dropping the row (see #4684).
                p_is_offline_replay: true,
              })
            : await supabase.from('attendance').insert({
                id: attendance.id,
                organization_id: attendance.organization_id,
                event_id: attendance.event_id,
                person_id: attendance.person_id,
                event_date: attendance.event_date,
                event_name: attendance.event_name,
                check_in_method: attendance.check_in_method,
                check_in_time: attendance.check_in_time,
                is_first_visit: resolvedFirstVisit,
                notes: attemptNotes,
                pickup_code: attemptCode,
              });
          if (!error) {
            insertError = undefined;
            if (isKidsRow) {
              kidsRpcResult = (attemptData ?? null) as { over_capacity_alert?: boolean } | null;
            }
            break;
          }
          insertError = error;
          if (attemptCode && isPickupCodeCollision(error) && collisionRetriesLeft > 0) {
            collisionRetriesLeft--;
            const newCode = generatePickupCode();
            // Rewrite the code embedded in notes too, so the now-dead code does
            // not linger — the checkout notes-fallback would otherwise keep
            // matching the original (reissued-away) code.
            attemptNotes = attemptNotes?.match(/Pickup code:\s*\d+/i)
              ? attemptNotes.replace(/Pickup code:\s*\d+/i, `Pickup code: ${newCode}`)
              : attemptNotes;
            attemptCode = newCode;
            codeReissued = true;
            // Key by the ORIGINAL family code so all siblings (and any further
            // reissue of this same code) converge on the final replacement.
            if (familyCode) pickupCodeRemap.set(familyCode, newCode);
            continue;
          }
          // #5903 round-10 P2: same reservation-room-changed race as
          // useOfflineAttendance's own flush loop — retry once with
          // identical fields (id unchanged) rather than leaving this row
          // failed until the next sync pass. Independent budget from the
          // collision retry above (round-11 P2) so a collision and a race
          // on the same row's attempts don't compete for one shared retry.
          if (isKidsRow && isRetryableCheckInRaceError(error) && raceRetriesLeft > 0) {
            raceRetriesLeft--;
            continue;
          }
          break;
        }
        if (!insertError && codeReissued) {
          // The family's original label/code is now invalid after the collision
          // reissue. Surface the NEW code so staff can reprint the label.
          toast.message("Pickup code reissued during sync", {
            description: `This check-in's pickup code changed to ${attemptCode}. Reprint the label with the new code.`,
            duration: 10000,
          });
        }
        if (kidsRpcResult?.over_capacity_alert) {
          // Loud, sticky alert — a queued check-in replayed into a room
          // that's now over capacity. The child IS present; this is a
          // staffing/ratio problem to act on, not a failed sync.
          toast.error('Room is over capacity', {
            description: `${attendance.event_name} synced a check-in that pushed this room over its listed limit while offline. Recount the room now.`,
            duration: Infinity,
          });
        }

        const error = insertError;
        if (error) {
          // Check if it's a duplicate (already synced)
          if (error.code === '23505') {
            await markAsSynced(STORES.PENDING_ATTENDANCE, attendance.id);
            successCount++;
          } else {
            throw error;
          }
        } else {
          // Audit via the sanctioned log_audit_event RPC, not a direct
          // audit_logs insert. audit_logs has RLS-on + no INSERT policy, so the
          // direct insert was silently denied (42501) for EVERY user — the trail
          // was never written. log_audit_event self-authorizes by org membership
          // (forcing user_id = auth.uid()), so it also works for volunteers.
          const { error: auditError } = await supabase.rpc('log_audit_event', {
            p_table_name: 'attendance',
            p_record_id: attendance.id,
            p_action: 'INSERT',
            p_organization_id: attendance.organization_id,
            p_old_data: null,
            p_new_data: {
              id: attendance.id,
              organization_id: attendance.organization_id,
              event_id: attendance.event_id,
              person_id: attendance.person_id,
              room_id: attendance.room_id ?? null,
              event_date: attendance.event_date,
              event_name: attendance.event_name,
              check_in_method: attendance.check_in_method,
              check_in_time: attendance.check_in_time,
              is_first_visit: resolvedFirstVisit,
              notes: attemptNotes,
              pickup_code: attemptCode,
            },
          });

          if (auditError) {
            console.error('Failed to write attendance audit log:', attendance.id, auditError);
          }
          await markAsSynced(STORES.PENDING_ATTENDANCE, attendance.id);
          successCount++;
        }
      } catch (error) {
        console.error('Failed to sync attendance:', attendance.id, error);
        errorCount++;
      }
    }

    return { successCount, errorCount, conflictCount };
  }, [user?.id]);

  const syncTasks = useCallback(async (): Promise<SyncResult> => {
    const pendingTasks = await getAllFromStore<PendingTask>(STORES.PENDING_TASKS);
    const unsyncedTasks = pendingTasks.filter(t => !t.synced);

    let successCount = 0;
    let errorCount = 0;
    const conflictCount = 0;

    for (const task of unsyncedTasks) {
      try {
        const { error } = await supabase.from('tasks').insert({
          id: task.id,
          organization_id: task.organization_id,
          title: task.title,
          description: task.description,
          priority: task.priority,
          due_date: task.due_date,
          project_id: task.project_id,
          request_id: task.request_id,
          status: 'todo',
        });

        if (error) {
          // Check if it's a duplicate (already synced)
          if (error.code === '23505') {
            await markAsSynced(STORES.PENDING_TASKS, task.id);
            successCount++;
          } else {
            throw error;
          }
        } else {
          await markAsSynced(STORES.PENDING_TASKS, task.id);
          successCount++;
        }
      } catch (error) {
        console.error('Failed to sync task:', task.id, error);
        errorCount++;
      }
    }

    return { successCount, errorCount, conflictCount };
  }, []);

  const syncAssignmentUpdates = useCallback(async (): Promise<SyncResult> => {
    const pendingUpdates = await getAllFromStore<PendingAssignmentUpdate>(STORES.PENDING_ASSIGNMENT_UPDATES);
    const unsyncedUpdates = pendingUpdates.filter(update => !update.synced);

    let successCount = 0;
    let errorCount = 0;
    let conflictCount = 0;

    for (const update of unsyncedUpdates) {
      try {
        const { data: existing, error: existingError } = await supabase
          .from('volunteer_assignments')
          .select('id, status, updated_at, confirmed_at, organization_id')
          .eq('id', update.assignment_id)
          .maybeSingle();

        if (existingError) throw existingError;

        if (!existing) {
          if (update.organization_id && user?.id) {
            // Through log_audit_event: audit_logs has no client INSERT policy, so a direct insert
            // here was always refused and the entry lost (#7849).
            await logAudit({
              tableName: 'volunteer_assignments',
              recordId: update.assignment_id,
              action: 'OFFLINE_SKIP',
              organizationId: update.organization_id,
              newData: {
                reason: 'assignment_missing',
                attempted_update: update,
              },
            });
          }
          await markAsSynced(STORES.PENDING_ASSIGNMENT_UPDATES, update.id);
          conflictCount++;
          continue;
        }

        const serverUpdatedAt = new Date(existing.updated_at).getTime();
        const clientUpdatedAt = new Date(update.created_offline).getTime();
        const clientWins = Number.isNaN(serverUpdatedAt)
          || Number.isNaN(clientUpdatedAt)
          || clientUpdatedAt >= serverUpdatedAt;

        if (!clientWins) {
          if (update.organization_id && user?.id) {
            await logAudit({
              tableName: 'volunteer_assignments',
              recordId: update.assignment_id,
              action: 'OFFLINE_CONFLICT',
              organizationId: update.organization_id,
              oldData: {
                status: update.status,
                updated_at: update.created_offline,
              },
              newData: {
                status: existing.status,
                updated_at: existing.updated_at,
                resolution: 'server_wins',
              },
            });
          }
          await markAsSynced(STORES.PENDING_ASSIGNMENT_UPDATES, update.id);
          conflictCount++;
          continue;
        }

        const responseAction = update.status === 'confirmed'
          ? 'accept'
          : update.status === 'declined'
            ? 'decline'
            : null;
        if (!responseAction) {
          throw new Error(`Unsupported assignment response status: ${update.status}`);
        }

        const confirmedAt = update.status === 'confirmed'
          ? update.confirmed_at ?? update.created_offline
          : null;
        const { error: updateError } = await supabase.rpc(
          'respond_to_volunteer_assignment' as never,
          buildRespondRpcArgs(update.assignment_id, responseAction) as never,
        );

        if (updateError) throw updateError;

        if (update.organization_id && user?.id) {
          await logAudit({
            tableName: 'volunteer_assignments',
            recordId: update.assignment_id,
            action: 'UPDATE',
            organizationId: update.organization_id,
            oldData: {
              status: existing.status,
              confirmed_at: existing.confirmed_at,
              updated_at: existing.updated_at,
            },
            newData: {
              status: update.status,
              confirmed_at: confirmedAt,
              updated_at: update.created_offline,
              offline_sync: true,
            },
          });
        }

        await markAsSynced(STORES.PENDING_ASSIGNMENT_UPDATES, update.id);
        successCount++;
      } catch (error) {
        console.error('Failed to sync assignment update:', update.assignment_id, error);
        errorCount++;
      }
    }

    return { successCount, errorCount, conflictCount };
  }, [user?.id]);

  const performSync = useCallback(async () => {
    if (syncState.isSyncing || !isOnline) return;

    const pendingCount = await safeGetPendingCount();
    if (pendingCount === null || pendingCount === 0) return;

    // KC-E2 (Codex #2898): capture which orgs had queued check-ins BEFORE draining,
    // so we can update the shared per-org clean-sync signal the connection badge
    // reads — this global drain (not just the kiosk hook) must move the badge.
    const attendanceOrgsBefore = new Set<string>(
      (await getAllFromStore<PendingAttendance>(STORES.PENDING_ATTENDANCE))
        .filter((record) => !record.synced && record.organization_id)
        .map((record) => record.organization_id),
    );

    setSyncState(prev => ({ ...prev, isSyncing: true, syncError: null }));

    try {
      const [attendanceResult, taskResult, assignmentResult] = await Promise.all([
        syncAttendance(),
        syncTasks(),
        syncAssignmentUpdates(),
      ]);

      const totalSuccess = attendanceResult.successCount + taskResult.successCount + assignmentResult.successCount;
      const totalErrors = attendanceResult.errorCount + taskResult.errorCount + assignmentResult.errorCount;
      const totalConflicts = attendanceResult.conflictCount + taskResult.conflictCount + assignmentResult.conflictCount;

      // KC-E2 (Codex #2898): stamp/clear the per-org clean-sync signal. "Clean" is
      // decided PER ORG by whether that org still has records pending after the
      // drain — NOT the batch-wide errorCount, which would let one org's failure
      // suppress a fully-reconciled org's badge. A failed record stays pending (it
      // is never markAsSynced'd), so "no remaining pending for this org" is exactly
      // the per-org clean signal.
      const attendancePendingAfter = (
        await getAllFromStore<PendingAttendance>(STORES.PENDING_ATTENDANCE)
      ).filter((record) => !record.synced);
      for (const orgId of attendanceOrgsBefore) {
        const orgClean = !attendancePendingAfter.some((record) => record.organization_id === orgId);
        if (orgClean) setLastCleanSync(orgId);
        else clearLastCleanSync(orgId);
      }

      if (totalSuccess > 0) {
        toast.success(`Synced ${totalSuccess} offline entries`, {
          description: [
            totalErrors > 0 ? `${totalErrors} failed to sync` : null,
            totalConflicts > 0 ? `${totalConflicts} resolved with newer changes` : null,
          ]
            .filter(Boolean)
            .join(' • ') || undefined,
        });
      } else if (totalErrors > 0) {
        toast.info(`Some items still need to sync`, {
          description: `We'll retry ${totalErrors} item${totalErrors === 1 ? "" : "s"} automatically.`,
        });
      }

      const newPendingCount = await safeGetPendingCount();
      if (newPendingCount === null) {
        setSyncState(prev => ({
          ...prev,
          isSyncing: false,
          lastSyncAt: new Date(),
          syncError: 'Sync completed, but pending count could not be refreshed.',
        }));
        return;
      }
      setSyncState(prev => ({
        ...prev,
        isSyncing: false,
        pendingCount: newPendingCount,
        lastSyncAt: new Date(),
        syncError:
          totalErrors > 0
            ? `${totalErrors} item${totalErrors === 1 ? "" : "s"} still pending.`
            : null,
      }));
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncState(prev => ({
        ...prev,
        isSyncing: false,
        syncError: 'Sync paused. We will retry automatically when you are online.',
      }));
    }
  }, [isOnline, safeGetPendingCount, syncState.isSyncing, syncAttendance, syncTasks, syncAssignmentUpdates]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && wasOffline) {
      // Delay slightly to ensure network is stable
      const timer = setTimeout(() => {
        performSync();
      }, OFFLINE_SYNC_ONLINE_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, performSync]);

  return {
    ...syncState,
    isOnline,
    performSync,
  };
}
